"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAuthGuard = useAuthGuard;
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const react_i18next_1 = require("react-i18next");
/**
 * 認證守衛 hook，用於保護需要認證的頁面
 *
 * @param authContext - 認證上下文值，包含 isAuthenticated, user, isLoading
 * @param options - 配置選項
 * @returns 認證狀態資訊
 *
 * @example
 * ```tsx
 * // 在組件中使用
 * const userContext = useContext(UserContext);
 * const { isAuthorized, isLoading } = useAuthGuard(
 *   {
 *     isAuthenticated: userContext?.isAuthenticated ?? false,
 *     user: userContext?.user || null,
 *     isLoading: userContext?.isLoading ?? false,
 *   },
 *   {
 *     allowedRoles: ['ADMIN', 'SUPER_ADMIN'],
 *     toast: { error: toast.error },
 *   }
 * );
 *
 * if (isLoading) return <Loading />;
 * if (!isAuthorized) return null;
 * ```
 */
function useAuthGuard(authContext, options = {}) {
    const { t } = (0, react_i18next_1.useTranslation)();
    const navigate = (0, react_router_dom_1.useNavigate)();
    const { requireAuth = true, allowedRoles = ['SUPER_ADMIN', 'ADMIN', 'USER'], redirectTo = '/login', showError = true, toast, } = options;
    const { isAuthenticated, user, isLoading } = authContext;
    // 檢查用戶是否有權限
    const isAuthorized = !requireAuth || Boolean(isAuthenticated &&
        user?.role &&
        allowedRoles.includes(user.role));
    (0, react_1.useEffect)(() => {
        // 如果正在加載用戶信息，等待完成
        if (isLoading)
            return;
        // 檢查認證狀態
        if (requireAuth && !isAuthenticated) {
            if (showError && toast) {
                toast.error(t('common:messages.unauthorized', 'Unauthorized'));
            }
            navigate(redirectTo);
            return;
        }
        // 檢查角色權限
        if (requireAuth && isAuthenticated && user?.role && !allowedRoles.includes(user.role)) {
            if (showError && toast) {
                toast.error(t('common:messages.accessDenied', 'Access denied'));
            }
            navigate('/');
            return;
        }
    }, [
        isLoading,
        requireAuth,
        isAuthenticated,
        user?.role,
        allowedRoles,
        navigate,
        redirectTo,
        showError,
        toast,
        t
    ]);
    return {
        isAuthenticated,
        user,
        isAuthorized,
        isLoading
    };
}
exports.default = useAuthGuard;
